import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";
import useAsyncEffect from "use-async-effect";

import Form from "@covertree/ui/forms/Form";
import FormCodeField from "@covertree/ui/forms/FormCodeField";
import FormTextField from "@covertree/ui/forms/FormTextField";
import FormPasswordField from "@covertree/ui/forms/FormPasswordField";
import ResendCodeButton from "@covertree/ui/buttons/ResendCodeButton";
import SubmitButton from "@covertree/ui/buttons/SubmitButton";

import {
  type CreateNewPasswordFormData,
  defaultValues,
  validationSchema,
} from "./form";

interface Props {
  submit: (values: CreateNewPasswordFormData) => void;
  secondaryAction: () => void;
  isPasswordReset: boolean;
}

export const CreateNewPasswordForm = ({
  submit,
  secondaryAction,
  isPasswordReset,
}: Props) => {
  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { isValid, isSubmitting },
  } = useForm({
    defaultValues,
    mode: "all",
    resolver: yupResolver(validationSchema(isPasswordReset)),
  });

  const formValue = watch();

  useAsyncEffect(async () => {
    if (formValue.password && formValue.confirmPassword)
      await trigger("confirmPassword");
  }, [formValue.password, trigger]);

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Grid container>
        {isPasswordReset && (
          <Grid item xs={12}>
            <FormCodeField
              id="code-field"
              dataTestId="code-field"
              name="code"
              label="Verification code"
              control={control}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormPasswordField
            id="password-field"
            dataTestId="password-field"
            name="password"
            label="New password"
            type="password"
            control={control}
          />
        </Grid>
        <Grid item xs={12}>
          <FormTextField
            id="confirm-password-field"
            dataTestId="confirm-password-field"
            name="confirmPassword"
            label="Confirm new password"
            type="password"
            control={control}
          />
        </Grid>

        <SubmitButton
          loading={isSubmitting}
          disabled={!isValid || isSubmitting}>
          Create new password
        </SubmitButton>
        {isPasswordReset && <ResendCodeButton onResendCode={secondaryAction} />}
      </Grid>
    </Form>
  );
};

export default CreateNewPasswordForm;
